/**
 * Zoekveld met suggesties op de winkelpagina.
 *
 * Enter zonder gemarkeerde suggestie verstuurt het formulier en levert dus een
 * resultatenpagina op; Enter op een gemarkeerde suggestie opent dat ene product.
 */

const MIN_LENGTH = 2;
const DEBOUNCE = 250;

const escapeHtml = (value) =>
  String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (character) =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character]
  );

// Markeert de getypte woorden in de productnaam.
const highlight = (title, query) => {
  const words = query
    .split(/[\s,;]+/)
    .filter((word) => word.length >= MIN_LENGTH)
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  const safe = escapeHtml(title);

  if (!words.length) {
    return safe;
  }

  return safe.replace(new RegExp(`(${words.join('|')})`, 'gi'), '<mark>$1</mark>');
};

class ProductSearch {
  constructor(form) {
    this.form = form;
    this.input = form.querySelector('.product-search__input');
    this.panel = form.querySelector('.product-search__panel');
    this.clearButton = form.querySelector('.product-search__clear');
    this.ajaxUrl = window.cart_object && window.cart_object.ajax_url;

    this.items = [];
    this.active = -1;
    this.request = null;
    this.timer = null;
    this.lastQuery = '';

    this.bind();
    this.toggleClear();
  }

  bind() {
    this.input.addEventListener('input', () => {
      this.toggleClear();
      this.schedule();
    });

    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
    this.input.addEventListener('focus', () => {
      if (this.items.length) {
        this.open();
      }
    });

    this.form.addEventListener('submit', (event) => {
      if (!this.input.value.trim()) {
        event.preventDefault();
      }
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.lastQuery = '';
        this.items = [];
        this.close();
        this.toggleClear();
        this.input.focus();
      });
    }

    // pointerdown i.p.v. click: iOS Safari stuurt geen click naar document bij een
    // tik op iets dat niet klikbaar is, waardoor het paneel dan nooit sloot.
    document.addEventListener('pointerdown', (event) => {
      if (!this.form.contains(event.target)) {
        this.close();
      }
    });

    // Safari (en andere browsers met een back-forward cache) zet de pagina bij
    // "terug" precies zo terug als hij was, dus met het paneel nog open. Sluit het
    // vóór het weggaan en ruim de toestand op bij terugkomst.
    window.addEventListener('pagehide', () => this.close());
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.reset();
      }
    });
  }

  reset() {
    window.clearTimeout(this.timer);

    if (this.request) {
      this.request.abort();
      this.request = null;
    }

    this.panel.classList.remove('is-loading');
    this.lastQuery = '';
    this.items = [];
    this.close();
    this.toggleClear();
  }

  toggleClear() {
    if (this.clearButton) {
      this.clearButton.hidden = !this.input.value;
    }
  }

  schedule() {
    window.clearTimeout(this.timer);

    const query = this.input.value.trim();

    if (query.length < MIN_LENGTH) {
      this.items = [];
      // lastQuery meeresetten, anders wordt dezelfde term daarna geweigerd als duplicaat
      this.lastQuery = '';
      this.close();
      return;
    }

    this.timer = window.setTimeout(() => this.fetch(query), DEBOUNCE);
  }

  fetch(query) {
    if (!this.ajaxUrl || query === this.lastQuery) {
      return;
    }

    // Een oudere, trager antwoordende request mag een nieuwe niet overschrijven.
    if (this.request) {
      this.request.abort();
    }

    this.lastQuery = query;
    this.panel.classList.add('is-loading');

    const params = new URLSearchParams({ action: 'searchproduct', search: query });

    this.request = new AbortController();

    window
      .fetch(`${this.ajaxUrl}?${params.toString()}`, {
        signal: this.request.signal,
        credentials: 'same-origin',
      })
      .then((response) => response.json())
      .then((data) => {
        this.panel.classList.remove('is-loading');
        this.render(data, query);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          this.panel.classList.remove('is-loading');
          this.close();
        }
      });
  }

  render(data, query) {
    const items = (data && data.items) || [];
    const total = (data && data.total) || 0;

    this.items = items;
    this.active = -1;

    if (!items.length) {
      const suggestion = data && data.suggestion;

      this.panel.innerHTML = `
        <p class="product-search__empty">
          Geen producten gevonden voor &ldquo;${escapeHtml(query)}&rdquo;.
          ${suggestion ? `Bedoelde je <button type="button" class="product-search__did-you-mean">${escapeHtml(suggestion)}</button>?` : ''}
        </p>
      `;

      const didYouMean = this.panel.querySelector('.product-search__did-you-mean');

      if (didYouMean) {
        didYouMean.addEventListener('click', () => {
          this.input.value = didYouMean.textContent;
          this.toggleClear();
          this.schedule();
        });
      }

      this.open();
      return;
    }

    const rows = items
      .map(
        (item, index) => `
        <a class="product-search__item"
           id="${this.panel.id}-option-${index}"
           role="option"
           aria-selected="false"
           href="${escapeHtml(item.url)}">
          <span class="product-search__item__image">
            <img src="${escapeHtml(item.image)}" alt="" loading="lazy">
          </span>
          <span class="product-search__item__body">
            <span class="product-search__item__title">${highlight(item.title, query)}</span>
            ${item.category ? `<span class="product-search__item__meta">${escapeHtml(item.category)}</span>` : ''}
          </span>
          ${item.price ? `<span class="product-search__item__price">${escapeHtml(item.price)}</span>` : ''}
        </a>
      `
      )
      .join('');

    this.panel.innerHTML = `
      ${rows}
      <button type="submit" class="product-search__all">
        <i class="fas fa-arrow-right" aria-hidden="true"></i>
        Bekijk alle ${total} ${total === 1 ? 'resultaat' : 'resultaten'}
      </button>
    `;

    this.open();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }

    const options = this.options();

    if (!options.length) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const next = this.active + direction;

      // Voorbij het laatste item valt de markering weg, zodat Enter weer zoekt.
      this.highlightOption(next < -1 ? options.length - 1 : next >= options.length ? -1 : next);
      return;
    }

    if (event.key === 'Enter' && this.active > -1) {
      event.preventDefault();
      window.location.href = options[this.active].getAttribute('href');
    }
  }

  options() {
    return Array.from(this.panel.querySelectorAll('.product-search__item'));
  }

  highlightOption(index) {
    const options = this.options();

    options.forEach((option) => {
      option.classList.remove('is-active');
      option.setAttribute('aria-selected', 'false');
    });

    this.active = index;

    if (index < 0) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }

    const option = options[index];
    option.classList.add('is-active');
    option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  open() {
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.active = -1;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('.product-search')
    .forEach((form) => new ProductSearch(form));
});
